import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import type { BillingUsageMeter, MeterPricingTiers } from './billing-meter.types';

export { BillingUsageMeter };
export type { MeterPricingTiers };

@Component({
  selector: 'tf-billing-meter',
  imports: [CommonModule, TranslateModule],
  templateUrl: './billing-meter.html',
  styleUrl: './billing-meter.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingMeter {
  readonly meter = input.required<BillingUsageMeter>();
  readonly showPricing = output<MeterPricingTiers>();

  protected readonly hasIncludedTick = computed(() => {
    const m = this.meter();
    return (
      m.capKind !== 'unlimited' &&
      m.included != null &&
      m.included > 0 &&
      m.limit != null &&
      m.included < m.limit
    );
  });

  /**
   * Fully unmetered: `unlimited` cap kind AND no free-tier `included` quota. Progress against
   * nothing isn't meaningful, so the bar swaps to a striped "unbounded" treatment and an
   * "Unlimited" badge appears next to the label. The label still carries the raw `used` figure
   * so the user sees consumption even without a cap to compare against.
   */
  protected readonly isFullyUnmetered = computed(() => {
    const m = this.meter();
    return m.capKind === 'unlimited' && (m.included == null || m.included <= 0);
  });

  protected onShowPricing(): void {
    const tiers = this.meter().pricingTiers;
    if (tiers) {
      this.showPricing.emit(tiers);
    }
  }
}
